import { patchState, signalStore, withMethods, withState } from '@ngrx/signals'

import { HarnessMessageFeedback } from '@flowx/designer/modules/ai-builder/types/harness.types'
import { harnessErrorMessage } from '@flowx/designer/modules/ai-builder/utils/harness-error.util'

type HarnessFeedbackState = {
  /** The maker's own feedback, keyed by message id, as the last session read returned it. Its own
   * slice rather than a field on `record`, which a `lifecycle` frame rewrites without its events
   * — and so without its feedback — at the end of every turn. */
  feedback: Readonly<Record<string, HarnessMessageFeedback>>
  /** Messages whose last feedback could not be sent, so the row can say so where it happened rather
   * than as a toast over the conversation. Cleared by the next attempt on that message. */
  feedbackErrors: Readonly<Record<string, string>>
}

const initialFeedbackState: HarnessFeedbackState = { feedback: {}, feedbackErrors: {} }

/** The maker's feedback on the conversation's messages. Session reads and clears are handed in;
 * this leaf owns the optimistic writes, without knowing how the conversation is carried. */
export const HarnessFeedbackStore = signalStore(
  { providedIn: 'root' },
  withState(initialFeedbackState),
  withMethods((store) => {
    // Feedback sent and not yet answered, by message id. Not in state: nothing renders it, and
    // it exists only so a session read cannot undo a write it was too early to have seen.
    const unansweredFeedback = new Map<string, HarnessMessageFeedback | null>()

    /** The one patch behind every change to a message's feedback, in either direction: the
     * feedback itself, and the error the row shows when the harness would not take it. */
    const setFeedback = (
      messageId: string,
      feedback: HarnessMessageFeedback | null,
      error?: string
    ): void =>
      patchState(store, (state) => ({
        feedback: feedback
          ? { ...state.feedback, [messageId]: feedback }
          : withoutKey(state.feedback, messageId),
        feedbackErrors: error
          ? { ...state.feedbackErrors, [messageId]: error }
          : withoutKey(state.feedbackErrors, messageId),
      }))

    /**
     * Every change goes the same way round: written here first so the thumb answers the press,
     * then sent, and put back the way it was if the harness refuses it. A thumb that lights and
     * goes out says the feedback did not land; one that waits on the network says nothing.
     */
    const submitChange = (
      messageId: string,
      next: HarnessMessageFeedback | null,
      send: () => Promise<unknown>
    ): void => {
      const previous = store.feedback()[messageId] ?? null

      setFeedback(messageId, next)
      unansweredFeedback.set(messageId, next)

      send()
        .catch((error: unknown) => setFeedback(messageId, previous, harnessErrorMessage(error)))
        .finally(() => {
          // Only when nothing has been sent for this message since — a later change owns the
          // entry now, and clearing it here would expose that one to the read it is guarding
          // against.
          if (unansweredFeedback.get(messageId) === next) {
            unansweredFeedback.delete(messageId)
          }
        })
    }

    return {
      submitChange,
      reconcileRead: (feedback: HarnessFeedbackState['feedback']): void => {
        patchState(store, {
          // A read served before an outstanding write committed does not know about it, so it must
          // not be allowed to take it back off the screen — the write, not the read, is newer.
          // A pending clear removes the entry for the same reason.
          feedback: [...unansweredFeedback].reduce(
            (merged, [messageId, pending]) =>
              pending ? { ...merged, [messageId]: pending } : withoutKey(merged, messageId),
            { ...feedback }
          ),
        })
      },
      /** Showing a record replaces its feedback and drops the errors of the one before, unlike a
       * read beside a live stream. */
      replace: (feedback: HarnessFeedbackState['feedback']): void =>
        patchState(store, { feedback, feedbackErrors: {} }),
      reset: (): void => {
        unansweredFeedback.clear()
        patchState(store, initialFeedbackState)
      },
    }
  })
)

function withoutKey<T>(map: Readonly<Record<string, T>>, key: string): Record<string, T> {
  return Object.fromEntries(Object.entries(map).filter(([id]) => id !== key))
}
